import { createTheme } from '@mui/material/styles';

// Base theme that both preview and full extend
const baseCardTheme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    fontWeight: 600,
                    borderRadius: '12px',
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    WebkitFontSmoothing: 'subpixel-antialiased',
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#f5f5f5',
                },
            },
        },
    },
});

// Preview theme (used in DigitalCardPreview.js)
export const previewCardTheme = createTheme({
    ...baseCardTheme,
    card: {
        container: {
            maxWidth: '330px',
            margin: 'auto',
            height: '600px',
            boxSizing: 'border-box',
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
        },
        header: {
            height: '140px',
            padding: '1rem',
            paddingBottom: '2rem',
            overlay: 'rgba(0, 0, 0, 0.15)'
        },
        profileCard: {
            margin: '-1.2rem 1rem 1.2rem',
            borderRadius: '0.8rem',
            padding: '0.8rem',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            width: '90%',
            maxWidth: '300px',
            position: 'absolute',
            top: 'calc(80px - 30px)',
            left: 0,
            right: 0,
            margin: '0 auto',
            zIndex: 15,
            marginBottom: '20px'
        },
        avatar: {
            border: '3px solid white',
            boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
            borderRadius: '0.4rem',
            width: '25%',
            minHeight: '60px'
        },
        companyLogo: {
            size: '25px',
            border: '2px solid white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            position: 'absolute',
            top: '8px',
            right: '8px'
        },
        aboutContainer: {
            backgroundColor: '#fff',
            borderTopLeftRadius: '1.2rem',
            borderTopRightRadius: '1.2rem',
            paddingTop: '50px',
            paddingBottom: '0.8rem',
            position: 'relative',
            zIndex: 2,
            marginTop: '-30px'
        },
        tabs: {
            margin: '0 auto 1rem',
            width: 'calc(100% - 2rem)',
            borderRadius: '0.4rem',
            padding: '0.2rem',
            display: 'flex',
            justifyContent: 'space-around',
            boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
            position: 'relative',
            zIndex: 20,
        },
        tabButton: {
            padding: '0.5rem 0.8rem',
            borderRadius: '0.4rem',
            fontSize: '0.8rem',
            flex: 1,
            textAlign: 'center',
            fontWeight: 600
        },
        bioSection: {
            padding: '0 1rem 1rem'
        },
        bioTypography: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
            textAlign: 'center',
            color: 'rgba(0,0,0,0.75)',
            whiteSpace: 'pre-wrap'
        },
        aboutSection: {
            padding: '0 1rem'
        },
        sectionTitle: {
            marginBottom: '0.8rem',
            fontSize: '0.7rem',
            color: 'rgba(0,0,0,0.4)',
            textAlign: 'center',
            textTransform: 'uppercase'
        },
        contactCard: {
            marginBottom: '0.8rem',
            padding: '0.8rem',
            borderRadius: '0.4rem',
            transition: 'transform 0.2s'
        },
        contactIcon: {
            width: '30px',
            height: '30px',
            borderRadius: '0.4rem',
            marginRight: '0.6rem'
        },
        contactLabel: {
            fontSize: '0.65rem',
            color: 'rgba(0,0,0,0.4)',
            textTransform: 'uppercase',
            fontWeight: 600,
            display: 'block'
        },
        contactValue: {
            fontSize: '0.8rem'
        }
    }
});

// Full card theme (used in Card.js)
export const fullCardTheme = createTheme({
    ...baseCardTheme,
    card: {
        container: {
            maxWidth: {
                xs: '95%',
                sm: '400px',
                md: '450px',
                lg: '500px',
                xl: '550px'
            },
            padding: '20px'
        },
        header: {
            height: '180px',
            padding: '1.5rem',
            paddingBottom: '3.5rem',
            overlay: 'rgba(0, 0, 0, 0.15)'
        },
        profileCard: {
            margin: '-2rem 1.5rem 1.8rem',
            borderRadius: '1rem',
            padding: '1rem',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            width: '90%',
            maxWidth: '340px',
            position: 'absolute',
            top: 'calc(120px - 40px)',
            left: 0,
            right: 0,
            margin: '0 auto',
            zIndex: 15,
            marginBottom: '30px'
        },
        avatar: {
            border: '4px solid white',
            boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
            borderRadius: '0.5rem',
            width: '30%',
            minHeight: '80px'
        },
        companyLogo: {
            size: '30px',
            border: '2px solid white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            position: 'absolute',
            top: '10px',
            right: '10px'
        },
        aboutContainer: {
            backgroundColor: '#fff',
            borderTopLeftRadius: '1.5rem',
            borderTopRightRadius: '1.5rem',
            paddingTop: '70px',
            paddingBottom: '1rem',
            position: 'relative',
            zIndex: 2,
            marginTop: '-40px'
        },
        tabs: {
            margin: '0 auto 1.5rem',
            width: 'calc(100% - 3rem)',
            borderRadius: '0.5rem',
            padding: '0.25rem',
            display: 'flex',
            justifyContent: 'space-around',
            boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
            position: 'relative',
            zIndex: 20,
        },
        tabButton: {
            padding: '0.6rem 1rem',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            flex: 1,
            textAlign: 'center',
            fontWeight: 600
        },
        bioSection: {
            padding: '0 1.5rem 1.5rem'
        },
        bioTypography: {
            fontSize: '0.9375rem',
            lineHeight: 1.6,
            textAlign: 'center',
            color: 'rgba(0,0,0,0.75)',
            whiteSpace: 'pre-wrap'
        },
        aboutSection: {
            padding: '0 1.5rem'
        },
        sectionTitle: {
            marginBottom: '1rem',
            fontSize: '0.75rem',
            color: 'rgba(0,0,0,0.4)',
            textAlign: 'center',
            textTransform: 'uppercase'
        },
        contactCard: {
            marginBottom: '1rem',
            padding: '1rem',
            borderRadius: '0.5rem',
            transition: 'transform 0.2s'
        },
        contactIcon: {
            width: '40px',
            height: '40px',
            borderRadius: '0.5rem',
            marginRight: '0.75rem'
        },
        contactLabel: {
            fontSize: '0.7rem',
            color: 'rgba(0,0,0,0.4)',
            textTransform: 'uppercase',
            fontWeight: 600,
            display: 'block'
        },
        contactValue: {
            fontSize: '0.875rem'
        }
    }
}); 